import { useState } from "react";
import { FilterState, PimsEvent, PimsProject } from "../types";

export function useFilterState() {
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filterState: FilterState = {
    searchText,
    startDate,
    endDate,
  };

  const clearFilters = () => {
    setSearchText("");
    setStartDate("");
    setEndDate("");
  };

  const filterEvents = (events: PimsEvent[]) => {
    return events
      .filter((event) => {
        if (!event) return false;

        // Filter by search text (event label, venue label, or venue city)
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          const matchesLabel = event.label?.toLowerCase().includes(searchLower);
          const matchesVenueLabel = event.venueLabel
            ?.toLowerCase()
            .includes(searchLower);
          const matchesVenueCity = event.venueCity
            ?.toLowerCase()
            .includes(searchLower);

          if (!matchesLabel && !matchesVenueLabel && !matchesVenueCity) {
            return false;
          }
        }

        // Filter by date range
        if (event.datetime) {
          const eventDate = new Date(event.datetime);

          if (startDate) {
            const start = new Date(startDate);
            if (eventDate < start) return false;
          }

          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999); // Include the entire end date
            if (eventDate > end) return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        // Sort by datetime descending (newest first)
        if (!a?.datetime && !b?.datetime) return 0;
        if (!a?.datetime) return 1;
        if (!b?.datetime) return -1;
        return new Date(b.datetime).getTime() - new Date(a.datetime).getTime();
      });
  };

  const filterProjects = (
    projects: PimsProject[],
    getProjectEvents: (id: string) => PimsEvent[]
  ) => {
    return projects
      .filter((project) => {
        if (!project) return false;

        // Filter by search text (project label)
        const projectMatches =
          !searchText ||
          project.label?.toLowerCase().includes(searchText.toLowerCase());

        // Check if project has events that match the filters
        const projectEvents = getProjectEvents(project.id);
        const filteredEvents = filterEvents(projectEvents);
        const hasMatchingEvents = filteredEvents.length > 0;

        // Show project if either:
        // 1. Project name matches search AND (no date filters OR has matching events)
        // 2. Project has events that match all filters
        return (
          (projectMatches &&
            (!startDate && !endDate ? true : hasMatchingEvents)) ||
          ((!searchText || projectMatches) && hasMatchingEvents)
        );
      })
      .sort((a, b) => {
        // Sort projects by their latest event datetime descending
        if (!a || !b) return 0;

        const aEvents = getProjectEvents(a.id);
        const bEvents = getProjectEvents(b.id);

        // Get latest datetime for each project
        const aLatestDate =
          aEvents
            .filter((event) => event?.datetime)
            .map((event) => new Date(event!.datetime!).getTime())
            .sort((x, y) => y - x)[0] || 0;

        const bLatestDate =
          bEvents
            .filter((event) => event?.datetime)
            .map((event) => new Date(event!.datetime!).getTime())
            .sort((x, y) => y - x)[0] || 0;

        return bLatestDate - aLatestDate;
      });
  };

  return {
    filterState,
    setSearchText,
    setStartDate,
    setEndDate,
    clearFilters,
    filterEvents,
    filterProjects,
  };
}
